// Dashboard landing page — WhatChimp design system parity (WP-00).
// Displays live stat cards, 5 quick action cards, usage quotas, recent campaigns
// with 4-metric progress bars, recent conversations, and AI assistant status.
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../lib/useAuth';
import { cn } from '../lib/cn';
import { useConversations } from '../lib/inbox';
import { useBroadcasts, type Campaign, type BcastMetric, type BcastStatus } from '../lib/broadcasts';
import { useUsageSummary } from '../lib/usage';
import {
  PageHeader,
  StatCard,
  Card,
  Button,
  Badge,
  EmptyState,
  Skeleton,
} from '../components/ui';
import {
  BroadcastingIcon,
  InboxIcon,
  BotIcon,
  ConnectIcon,
  SubscribersIcon,
  AdsIcon,
  SparklesIcon,
  ChevronRightIcon,
} from '../components/icons';

const STATUS_BADGE: Record<
  BcastStatus,
  { variant: 'slate' | 'warning' | 'info' | 'success' | 'danger'; dot: boolean }
> = {
  draft: { variant: 'slate', dot: true },
  scheduled: { variant: 'warning', dot: true },
  sending: { variant: 'info', dot: true },
  completed: { variant: 'success', dot: true },
  paused: { variant: 'warning', dot: true },
  cancelled: { variant: 'danger', dot: true },
  failed: { variant: 'danger', dot: true },
};

function MetricCell({
  metric,
  barColor,
  label,
}: {
  metric: BcastMetric;
  barColor: string;
  label: string;
}) {
  return (
    <td className="px-4 py-3 align-middle">
      <div className="min-w-[120px]">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-ink dark:text-slate-200">{label}</span>
          <span className="font-mono text-ink-3 dark:text-slate-400">{metric.pct}%</span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={cn('h-full rounded-full transition-all', barColor)}
            style={{ width: `${metric.pct}%` }}
          />
        </div>
        <div className="mt-0.5 font-mono text-[10px] text-ink-4 dark:text-slate-500">
          {metric.sub}
        </div>
      </div>
    </td>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversations } = useConversations();
  const { broadcasts, loading } = useBroadcasts();
  const { rings, subscribers, planId } = useUsageSummary();

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'there';
  const openConvos = conversations.filter((c) => c.status === 'open').length;
  const campaignCount = broadcasts.length;
  const sentCampaigns = broadcasts.filter((b) => b.recipients > 0);
  const openRate = sentCampaigns.length
    ? Math.round(sentCampaigns.reduce((s, b) => s + b.opened.pct, 0) / sentCampaigns.length)
    : 0;

  const subLimit = rings[0]?.limit ?? 0;
  const msgUsed = rings[1]?.used ?? 0;
  const msgLimit = rings[1]?.limit ?? 1;
  const msgPct = Math.min(100, Math.round((msgUsed / msgLimit) * 100));
  const subPct = subLimit ? Math.min(100, Math.round((subscribers / subLimit) * 100)) : 0;

  // Quick action cards (5 items matching WhatChimp)
  const quickActions = [
    {
      title: t('dash.action.newBroadcast'),
      sub: t('dash.action.newBroadcastSub'),
      icon: <BroadcastingIcon size={20} />,
      color: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
      path: '/broadcasting',
    },
    {
      title: t('dash.action.openInbox'),
      sub: t('dash.action.openInboxSub'),
      icon: <InboxIcon size={20} />,
      color: 'bg-brand-50 text-brand dark:bg-brand-50/15 dark:text-brand-tint',
      path: '/inbox',
    },
    {
      title: t('dash.action.createFlow'),
      sub: t('dash.action.createFlowSub'),
      icon: <BotIcon size={20} />,
      color: 'bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
      path: '/bots',
    },
    {
      title: t('dash.action.trainAi'),
      sub: t('dash.action.trainAiSub'),
      icon: <SparklesIcon size={20} />,
      color: 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
      path: '/bots',
    },
    {
      title: t('dash.action.connectChannel'),
      sub: t('dash.action.connectChannelSub'),
      icon: <ConnectIcon size={20} />,
      color: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
      path: '/connect',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome header with primary New Broadcast action */}
      <PageHeader
        tag={t('app.name')}
        title={t('dash.welcomeUser').replace('{name}', displayName)}
        subtitle={t('dash.subtitle')}
        action={
          <Button
            variant="primary"
            size="md"
            leadingIcon={<BroadcastingIcon size={16} />}
            onClick={() => navigate('/broadcasting')}
          >
            {t('dash.action.newBroadcast')}
          </Button>
        }
      />

      {/* Quick actions row (5 cards) */}
      <div>
        <div className="mb-3 text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
          {t('dash.quickActions')}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => navigate(action.path)}
              className="group flex flex-col justify-between rounded-card border border-line bg-white p-4 text-start shadow-card transition-all hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
            >
              <div
                className={cn(
                  'grid h-10 w-10 place-items-center rounded-full transition-transform group-hover:scale-105',
                  action.color,
                )}
                aria-hidden
              >
                {action.icon}
              </div>
              <div className="mt-4">
                <h4 className="text-[13px] font-bold text-ink dark:text-slate-100 group-hover:text-brand">
                  {action.title}
                </h4>
                <p className="mt-0.5 text-[11px] text-ink-3 dark:text-slate-400">{action.sub}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Key Stats Row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {loading ? (
          <>
            <Skeleton.StatCard />
            <Skeleton.StatCard />
            <Skeleton.StatCard />
            <Skeleton.StatCard />
          </>
        ) : (
          <>
            <StatCard
              label={t('dash.stat.subscribers')}
              value={subscribers.toLocaleString()}
              sub={subLimit ? `/ ${subLimit.toLocaleString()}` : undefined}
              variant="indigo"
              icon={<SubscribersIcon size={20} />}
              onClick={() => navigate('/subscribers')}
            />
            <StatCard
              label={t('dash.stat.conversations')}
              value={String(openConvos)}
              variant="green"
              icon={<InboxIcon size={20} />}
              onClick={() => navigate('/inbox')}
            />
            <StatCard
              label={t('dash.stat.campaigns')}
              value={String(campaignCount)}
              variant="rose"
              icon={<BroadcastingIcon size={20} />}
              onClick={() => navigate('/broadcasting')}
            />
            <StatCard
              label={t('dash.stat.openRate')}
              value={`${openRate}%`}
              variant="amber"
              icon={<AdsIcon size={20} />}
              onClick={() => navigate('/broadcasting')}
            />
          </>
        )}
      </div>

      {/* Usage & Quota progress bar card */}
      <Card
        title={t('dash.growth.title')}
        subtitle={t('dash.growth.subtitle')}
        headerAction={
          <Badge variant="brand" size="sm">
            {planId ? planId.toUpperCase() : 'TRIAL'}
          </Badge>
        }
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Subscribers quota bar */}
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {t('dash.stat.subscribers')}
              </span>
              <span className="font-mono font-bold text-ink dark:text-slate-200">
                {subscribers.toLocaleString()} / {subLimit.toLocaleString()} ({subPct}%)
              </span>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-full rounded-full bg-brand transition-all"
                style={{ width: `${subPct}%` }}
              />
            </div>
          </div>

          {/* Messages quota bar */}
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {t('dash.stat.campaigns')}
              </span>
              <span className="font-mono font-bold text-ink dark:text-slate-200">
                {msgUsed.toLocaleString()} / {msgLimit.toLocaleString()} ({msgPct}%)
              </span>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-full rounded-full bg-success transition-all"
                style={{ width: `${msgPct}%` }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Recent campaigns section */}
      <Card
        title={t('dash.recent')}
        headerAction={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/broadcasting')}
            trailingIcon={<ChevronRightIcon size={14} className="rtl:rotate-180" />}
          >
            {t('dash.viewAll')}
          </Button>
        }
        noPadding
      >
        {/* Desktop Table View (>= 768px) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-start text-[13px]">
            <thead>
              <tr className="border-b border-line bg-slate-50/70 dark:border-slate-800 dark:bg-slate-800/50">
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.num')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.campaign')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.status')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.processed')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.delivered')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.opened')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.unreached')}
                </th>
                <th className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                  {t('dash.col.scheduled')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line dark:divide-slate-800">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton.TableRow key={i} cols={8} />
                ))
              ) : (
                broadcasts.slice(0, 5).map((campaign: Campaign, i: number) => {
                  const badgeInfo = STATUS_BADGE[campaign.status] || {
                    variant: 'slate' as const,
                    dot: false,
                  };
                  return (
                    <tr
                      key={campaign.id}
                      className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-ink-3 dark:text-slate-400">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3 font-bold text-ink dark:text-slate-100">
                        {campaign.name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={badgeInfo.variant} size="sm" dot={badgeInfo.dot}>
                          {t(`status.${campaign.status}`)}
                        </Badge>
                      </td>
                      <MetricCell
                        metric={campaign.processed}
                        barColor="bg-brand"
                        label={t('dash.col.processed')}
                      />
                      <MetricCell
                        metric={campaign.delivered}
                        barColor="bg-success"
                        label={t('dash.col.delivered')}
                      />
                      <MetricCell
                        metric={campaign.opened}
                        barColor="bg-warning"
                        label={t('dash.col.opened')}
                      />
                      <MetricCell
                        metric={campaign.unreached}
                        barColor="bg-slate-400"
                        label={t('dash.col.unreached')}
                      />
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-ink-3 dark:text-slate-400">
                        {campaign.scheduledAt}
                      </td>
                    </tr>
                  );
                })
              )}
              {!loading && broadcasts.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-4">
                    <EmptyState
                      icon={<BroadcastingIcon size={24} />}
                      title={t('dash.empty')}
                      action={
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => navigate('/broadcasting')}
                        >
                          {t('dash.action.newBroadcast')}
                        </Button>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile List View (< 768px) */}
        <div className="md:hidden p-4 space-y-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton.Card key={i} className="p-3" />
            ))
          ) : broadcasts.length === 0 ? (
            <EmptyState
              icon={<BroadcastingIcon size={24} />}
              title={t('dash.empty')}
              action={
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/broadcasting')}
                >
                  {t('dash.action.newBroadcast')}
                </Button>
              }
            />
          ) : (
            broadcasts.slice(0, 5).map((campaign: Campaign) => {
              const badgeInfo = STATUS_BADGE[campaign.status] || {
                variant: 'slate' as const,
                dot: false,
              };
              return (
                <div
                  key={campaign.id}
                  className="rounded-lg border border-line p-3 dark:border-slate-800"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-ink dark:text-slate-100 text-sm">
                      {campaign.name || '—'}
                    </span>
                    <Badge variant={badgeInfo.variant} size="sm" dot={badgeInfo.dot}>
                      {t(`status.${campaign.status}`)}
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-ink-3 dark:text-slate-400 font-mono">
                    {campaign.scheduledAt}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* 2x2 cards: Recent Conversations & AI Assistant Status */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Conversations */}
        <Card
          title={t('dash.recentActivities')}
          headerAction={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/inbox')}
              trailingIcon={<ChevronRightIcon size={14} className="rtl:rotate-180" />}
            >
              {t('dash.viewAll')}
            </Button>
          }
          noPadding
        >
          {loading ? (
            <div className="p-4 space-y-3">
              <Skeleton.Card className="p-3" />
              <Skeleton.Card className="p-3" />
              <Skeleton.Card className="p-3" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<InboxIcon size={20} />}
                title={t('dash.recentActivitiesEmpty')}
              />
            </div>
          ) : (
            <div className="divide-y divide-line dark:divide-slate-800">
              {conversations.slice(0, 5).map((c) => (
                <div
                  key={c.id}
                  onClick={() => navigate('/inbox')}
                  className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-bold text-ink dark:bg-slate-800 dark:text-slate-200">
                      {c.initials || 'C'}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-xs font-bold text-ink dark:text-slate-100">
                        {c.name || c.phone}
                      </div>
                      <div className="truncate text-[11px] text-ink-3 dark:text-slate-400">
                        {c.preview || '—'}
                      </div>
                    </div>
                  </div>
                  <Badge variant={c.status === 'open' ? 'success' : 'slate'} size="sm" dot>
                    {c.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* AI Assistant Status Card */}
        <Card
          title={t('dash.aiStatus.title')}
          subtitle={t('dash.aiStatus.desc')}
          headerAction={
            <Badge variant="success" size="sm" dot>
              Online
            </Badge>
          }
        >
          <div className="flex flex-col justify-between gap-4">
            <div className="rounded-lg border border-line bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-800/40">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {t('topbar.aiTokens')}
                </span>
                <span className="font-mono font-bold text-ink dark:text-slate-200">
                  0 / {rings[2]?.limit ? rings[2].limit.toLocaleString() : '100,000'}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                <div className="h-full w-0 rounded-full bg-brand" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-ink-3 dark:text-slate-400">
                Claude 3.5 Sonnet Engine
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/bots')}
              >
                {t('dash.action.createFlow')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
